/**
 *
 * AudiencesManagedList
 *
 */
import React, { useState, useEffect, useCallback } from "react";
import classNames from "classnames";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

// assets
import { RetweetOutlined } from '@ant-design/icons';
// antd component
import { Button, Tooltip } from "antd";

// components
import ErrorBoundary from "engine/ErrorBoundary";
import CustomPageHeader from "components/CustomPageHeader";
import { NotificationSimple } from "components/Notifications";

// inner components
import { AudiencesTable, AudiencesAddNew } from "./components";

// utils
import api from "utils/api";
import { useAsync } from "utils/hooks";
import { isDateBeforeToday } from "utils/moment/moment.utils";
import {
  USER_TYPES_OPTIONS,
  AUDIENCE_TABLE_STATUS_OPTIONS,
} from "utils/helpers/helper.constant";
import {
  getObjId,
  ArrayEquals,
  getUniqueArrayValue,
  getUniqueArrayObj,
} from "utils/helpers/helper.general";

function AudiencesManagedList({
  editable,
  editEnabeled,
  isReminderLoading,
  // actions
  onOpenReminder,
  // default props
  className,
  ...rest
}) {
  const childClassNames = classNames("audiences-managed-list", className);
  const { t } = useTranslation();

  const newKey = "new";
  const { id: initId } = useParams();

  const [isEditEnabeled, setEditEnabeled] = useState(editEnabeled || false);
  const [communicationId, setCommunicationId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [hasFormOrAppointment, setHasFormOrAppointment] = useState(false);
  const [isExpired, setIsExpired] = useState(false);

  const [initAudiences, setInitAudiences] = useState(null);
  const [commAudiences, setCommAudiences] = useState(null);
  const [deletedIds, setDeletedIds] = useState({ students: [], users: [] });
  const [addedIds, setAddedIds] = useState({ students: [], users: [] });
  const [tableRows, setTableRow] = useState([]);

  const {
    data: dataCommAudiences,
    isLoading,
    isSuccess,
    run: getCommAudiences,
  } = useAsync();
  const {
    data: updatedAudiences,
    isLoading: isUpdateLoading,
    isSuccess: isUpdateSuccess,
    run: updateAudiences,
  } = useAsync();
  
  const {
    data: regenerateAudiences,
    isLoading: isRegenerateLoading,
    isSuccess: isRegenerateSuccess,
    run: runRegenerateAudiences,
  } = useAsync();

  // Because history not trigger update component
  useEffect(() => {
    if (!!initId && initId !== newKey) {
      setCommunicationId(initId);
      setCommAudiences(null);
      // Loading data for rendering in a component
      getAudiences(initId);
    }
  }, [initId]);

  // Set lodaded data for rendering in a component
  useEffect(() => {
    if (!!isUpdateSuccess) {
      onChangeMode(false);
    }
  }, [isUpdateSuccess]);

  // Generate Status for Table Rows from communication data
  // return: { label: '', value: '' }
  const generateStatus = useCallback(
    (itemData, initElement) => {
      let status = {
        label: "",
        value: "",
      };
      if (!!itemData && !!initElement) {
        const itemID = getObjId(itemData);
        const read = initElement?.studentsRead?.find(
          el => getObjId(el) == itemID,
        );
        const hasFA = dataCommAudiences?.types?.some(
          el => el == "forms" || el == "appointments",
        );
        const answered =
          hasFA &&
          initElement?.studentsAnswered?.find(el => getObjId(el) == itemID);
        if (!read && !answered) {
          status = {
            label: AUDIENCE_TABLE_STATUS_OPTIONS("notRead"),
            value: "notRead",
          };
        } else if (read && !answered) {
          status = {
            label: AUDIENCE_TABLE_STATUS_OPTIONS("read"),
            value: "read",
          };
        } else if ((!read || read) && answered) {
          status = {
            label: AUDIENCE_TABLE_STATUS_OPTIONS("answered"),
            value: "answered",
          };
        }
      }
      return status;
    },
    [hasFormOrAppointment, dataCommAudiences],
  );

  // return: init, members
  const prepearInitAndMembers = useCallback(
    initElement => {
      let currEl = {
        init: initElement || {},
        members: [],
      };
      if (!!initElement) {
        let students = [];
        let users = [];
        if (initElement?.students && initElement?.students?.length > 0) {
          students = initElement?.students
            ?.map(elem => {
              return {
                ...elem,
                type: USER_TYPES_OPTIONS("student"),
                typeInit: "students",
                firstname: elem?.description?.firstname,
                lastname: elem?.description?.lastname,
                status: generateStatus(elem, initElement),
              };
            })
            .sort((a, b) => a?.lastname?.localeCompare(b?.lastname));
        }

        if (initElement?.users && initElement?.users?.length > 0) {
          users = initElement?.users
            ?.map(elem => {
              return {
                ...elem,
                type: USER_TYPES_OPTIONS(elem?.role || "admin"),
                typeInit: "users",
                firstname: elem?.description?.firstname,
                lastname: elem?.description?.lastname,
              };
            })
            .sort((a, b) => a?.lastname?.localeCompare(b?.lastname));
        }

        currEl = {
          ...currEl,
          init: {
            ...initElement,
            students: students,
            users: users,
          },
          members: [...students, ...users],
        };
      }
      return currEl;
    },
    [hasFormOrAppointment, dataCommAudiences],
  );

  // Set lodaded data for rendering in a component
  useEffect(() => {
    if (!isLoading && !!dataCommAudiences) {
      const { init, members } = prepearInitAndMembers(dataCommAudiences);
      const hasFA = dataCommAudiences?.types?.some(
        el => el == "forms" || el == "appointments",
      );
      if (!!hasFA) {
        const expiredDeadlineDate = isDateBeforeToday(
          dataCommAudiences?.deadlineDate,
        );
        setHasFormOrAppointment(hasFA);
        setIsExpired(
          !!expiredDeadlineDate ||
            (!!!dataCommAudiences?.deadlineDate &&
              dataCommAudiences?.students?.length ==
                dataCommAudiences?.studentsAnswered?.length),
        );
      }

      setTableRow(members || []);
      setCommAudiences(init || dataCommAudiences);
      setInitAudiences(init || dataCommAudiences);
    }
  }, [dataCommAudiences]);

  // Set updated data for rendering in a component
  useEffect(() => {
    if (!isUpdateLoading && !!updatedAudiences && isUpdateSuccess) {
      const { init, members } = prepearInitAndMembers(updatedAudiences);
      const hasFA = dataCommAudiences?.types?.some(
        el => el == "forms" || el == "appointments",
      );
      if (!!hasFA) {
        const expiredDeadlineDate = isDateBeforeToday(
          dataCommAudiences?.deadlineDate,
        );
        setHasFormOrAppointment(hasFA);
        setIsExpired(
          !!expiredDeadlineDate ||
            (!!!dataCommAudiences?.deadlineDate &&
              dataCommAudiences?.students?.length ==
                dataCommAudiences?.studentsAnswered?.length),
        );
      }
      setTableRow(members || []);
      setCommAudiences(init || updatedAudiences);
      setInitAudiences(init || updatedAudiences);
      setDeletedIds({ students: [], users: [] });
      setAddedIds({ students: [], users: [] });
    }
  }, [updatedAudiences]);

  const onChangeMode = useCallback((mode = null) => {
    if (!!mode) {
      setEditEnabeled(mode);
    } else {
      setEditEnabeled(prev => !prev);
    }
  }, []);

  const onCancelEdit = useCallback(() => {
    onChangeMode();
    setCommAudiences();
    setDeletedIds({ students: [], users: [] });
    setAddedIds({ students: [], users: [] });
  }, [initAudiences, isEditEnabeled]);

  const getAudiences = useCallback(async communicationId => {
    if (!!communicationId) {
      await getCommAudiences(
        api.communications.getCommAudiencesById(communicationId),
      );
    }
  }, []);
  const forceUpdateAudiencesAndResentComm = useCallback(
    async communicationId => {
      if (!!communicationId) {
        await runRegenerateAudiences(
          api.communications.forceUpdateCommAudiencesAndSentById(
            communicationId,
          ),
        );
      }
    },
    [],
  );

  const onSaveAudiences = useCallback(async () => {
    if (!!isEditEnabeled && !!editable) {
      const sendObj = { deletedIds, addedIds };
      await updateAudiences(
        api.communications.updateCommAudiences(communicationId, sendObj),
      );
    }
  }, [isEditEnabeled, editable, deletedIds, addedIds]);
  // get props: { students: [object], users: [object], studentsID: ['_id'], usersID: ['_id'] }
  const onAddAudiences = useCallback(
    ({ students, users, studentsID, usersID }) => {
      if (!!isEditEnabeled && !!editable) {
        let newStds = addedIds?.students || [];
        let newUsersArr = addedIds?.users || [];
        if (!!studentsID && studentsID?.length > 0) {
          let stds = addedIds?.students || [];
          newStds = stds.concat(studentsID);
        }
        if (!!usersID && usersID?.length > 0) {
          let usersArr = addedIds?.users || [];
          newUsersArr = usersArr.concat(usersID);
        }

        const newSt = {
          students: newStds,
          users: newUsersArr,
        };

        if (
          !ArrayEquals(addedIds?.students, newSt?.students) ||
          !ArrayEquals(addedIds?.users, newSt?.users)
        ) {
          setAddedIds(newSt);
          setDeletedIds(prev => ({
            [students]:
              prev?.students?.filter(
                el => !newSt?.students?.includes(getObjId(el)),
              ) || [],
            [users]:
              prev?.users?.filter(
                el => !newSt?.users?.includes(getObjId(el)),
              ) || [],
          }));
        }

        let elementsStd = commAudiences?.students || [];
        let newElementsStd = elementsStd.concat(students);
        let elementsUsers = commAudiences?.users || [];
        let newElementsUsers = elementsUsers.concat(users);

        if (
          (!!students && students?.length > 0) ||
          (!!users && users?.length > 0)
        ) {
          const { init, members } = prepearInitAndMembers({
            ...commAudiences,
            students:
              getUniqueArrayObj(newElementsStd) || commAudiences?.students,
            users: getUniqueArrayObj(newElementsUsers) || commAudiences?.users,
          });
          setTableRow(members || []);
          setCommAudiences(init);
        }
      }
    },
    [isEditEnabeled, editable, deletedIds, addedIds, commAudiences],
  );

  // Remove and update current Element
  const removeElement = elementObj => {
    if (elementObj && elementObj?._id) {
      const typeInit = elementObj?.typeInit || "students";
      let elements = commAudiences[typeInit] || [];
      const { init, members } = prepearInitAndMembers({
        ...commAudiences,
        [typeInit]:
          elements?.filter(el => getObjId(el) != getObjId(elementObj)) || [],
      });
      setTableRow(members || []);
      setCommAudiences(init);

      setDeletedIds(prev => ({
        ...prev,
        [typeInit]: getUniqueArrayValue([...prev[typeInit], elementObj?._id]),
      }));
      setAddedIds(prev => ({
        ...prev,
        [typeInit]:
          prev[typeInit]?.filter(el => getObjId(el) != getObjId(elementObj)) ||
          [],
      }));
    }
  };

  return (
    <ErrorBoundary>
      <div className={childClassNames}>
        <CustomPageHeader
          className="audiences-managed-list__header"
          title={
            <>
              Liste d'envoi
              {!!editable && (
                <Tooltip
                  placement="bottom"
                  title={t("notifications.sendMsgToNewUser")}
                  color="#005d9f"
                  trigger="hover"
                >
                  <Button
                    type="default"
                    className="ant-btn-warn force-update"
                    disabled={
                      !!isRegenerateLoading && !isRegenerateSuccess ||
                      isLoading ||
                      isUpdateLoading ||
                      isReminderLoading
                    }
                    loading={!!isRegenerateLoading && !isRegenerateSuccess}
                    onClick={() =>
                      forceUpdateAudiencesAndResentComm(
                        communicationId || initId
                      )
                    }
                  >
                    <RetweetOutlined />
                    {t("audiences.updateList")}
                  </Button>
                </Tooltip>
              )}
            </>
          }
          subTitlePosition="middle"
          extraActions={
            !!editable && [
              hasFormOrAppointment &&
                !isEditEnabeled &&
                (!isExpired && !!dataCommAudiences?.deadlineDate ? (
                  <Tooltip
                    placement="top"
                    title={t("news.deadlineNotExpired")}
                    color="#005D9F"
                    trigger="click"
                  >
                    <Button
                      type="default"
                      className="ant-btn-cancel"
                      disabled={!isExpired || isReminderLoading}
                      onClick={() => onOpenReminder(true)}
                    >
                      {t("common.relancer")}
                    </Button>
                  </Tooltip>
                ) : (
                  <Button
                    type="default"
                    className="ant-btn-cancel"
                    disabled={isReminderLoading}
                    onClick={() => onOpenReminder(true)}
                  >
                    {t("common.relancer")}
                  </Button>
                )),
              <Button
                type="default"
                className={`${
                  isEditEnabeled ? "ant-btn-cancel" : "ant-btn-submit"
                }`}
                onClick={() =>
                  isEditEnabeled ? onCancelEdit() : onChangeMode()
                }
              >
                {isEditEnabeled ? t("common.cancel") : t("common.modify")}
              </Button>,
              !!isEditEnabeled && (
                <Button
                  className="ant-btn-save"
                  type="default"
                  onClick={onSaveAudiences}
                >
                  {t("common.save")}
                </Button>
              ),
            ]
          }
        />

        <AudiencesTable
          editable={editable}
          editMode={isEditEnabeled}
          dataSource={tableRows || []}
          isLoading={isLoading}
          onRemoveRow={removeElement}
        />

        <AudiencesAddNew
          open={showModal}
          setOpen={setShowModal}
          onAdd={onAddAudiences}
          editable={editable}
          isEditEnabeled={isEditEnabeled}
        />

        {!!editable && (
          <NotificationSimple
            Loading={isUpdateLoading}
            Successful={isUpdateSuccess}
            message={t("notifications.during")}
            messageSuccess={t("audiences.modifiedAudience")}
            description=""
            type="update"
          />
        )}
      </div>
    </ErrorBoundary>
  );
}

AudiencesManagedList.defaultProps = {
  editable: false,
  editEnabeled: false,
};

export default AudiencesManagedList;
